// src/api/artifacts.ts
import { get as storeGet, put as storePut, compositeKey } from "../utils/store";

export type ArtifactImage = { url: string; filename: string } | null;

export type Artifact = {
  day_number: number;
  title: string | null;
  emoji: string | null;
  mantra: string | null;
  short_benefit: string | null;
  share_text: string | null;
  image_master: ArtifactImage;
  image_story: ArtifactImage;
  image_feed: ArtifactImage;
  image_twitter: ArtifactImage;
};

// M5-STORE3 (18.09.2026): было — любой отказ глотался в [], "артефактов нет"
// и "не спросили" были неразличимы (офлайн давал "для этого дня артефакт не
// задан", хотя он задан — задание трека). Форма — как у courses.ts/snapshot.ts:
// трёхзначно + cached. "empty" покрывает и легитимно пустой список, и
// 404/400/403/401 — сервер ответил определённо, в кэш не пишем и не читаем.
// Транспорт НЕ менялся: голый fetch, без fetchWithAuth — записанное решение
// от 12.07.2026 ("осознанный размен ради шеринга", эндпоинт публичный); кэш
// добавлен поверх существующего способа спросить, не меняет его.
export type ArtifactsResult =
  | { status: "ok"; data: Artifact[] }
  | { status: "empty" }
  | { status: "unavailable" }
  | { status: "cached"; data: Artifact[]; cachedAt: number; stale: boolean };

/** Спросить кэш при сетевом отказе. Пусто в кэше → unavailable, как раньше. */
async function cachedOrUnavailable(key: string): Promise<ArtifactsResult> {
  const cached = await storeGet<Artifact[]>(key);
  if (cached.status === "empty") return { status: "unavailable" };
  console.error(`[artifacts] served from cache, age=${Date.now() - cached.cachedAt}`);
  return {
    status: "cached",
    data: cached.data,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

export async function fetchArtifacts(courseId: number): Promise<ArtifactsResult> {
  const key = compositeKey("artifacts", courseId);

  let res: Response;
  try {
    res = await fetch(`/api/courses/${courseId}/artifacts`);
  } catch (e) {
    console.error("[artifacts] network error:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }

  if (res.status === 404 || res.status === 400 || res.status === 403 || res.status === 401) {
    return { status: "empty" };
  }

  if (!res.ok) {
    console.error("[artifacts] unexpected error status:", res.status);
    return cachedOrUnavailable(key);
  }

  try {
    const json = await res.json();
    const items: Artifact[] = Array.isArray(json?.items) ? json.items : [];
    if (items.length === 0) return { status: "empty" };
    await storePut(key, items, 0);
    return { status: "ok", data: items };
  } catch (e) {
    console.error("[artifacts] 200 but json parse failed:", e instanceof Error ? e.message : String(e));
    return { status: "empty" };
  }
}
